// gardening: make these apps support the core package so duplicates can be removed.
// https://linear.app/reposit/issue/PRO-3671
// Same display rule as packages/core/src/client/util/charge-item-display.ts; keep the two in step.
const CHARGE_TYPE_LABELS: Record<string, string> = {
  DAMAGE: 'Damage',
  CLEANING: 'Cleaning',
  RENT_ARREARS: 'Rent arrears',
  ITEM_REMOVAL: 'Item removal',
  OTHER: 'Other',
};

export const CHARGE_TITLE_MAX_LENGTH = 50;

export interface ChargeItemText {
  title?: string | null;
  description?: string | null;
  type: string;
}

export interface ChargeItemDisplay {
  title: string;
  description: string | null;
}

// Stripped from the end of a title cut from a description, so it doesn't end in "," or " -".
const TITLE_TRAILING_PUNCTUATION = /[ .,;:-]+$/;

/**
 * The title for a description too long to be the title itself, cut without breaking a word. The backfill
 * saves the same title. In order: the first line if it is 15 to 50 characters, then the first sentence if
 * it is, then as many whole words as fit with "…" after them. A first word too long to fit is cut, with "…".
 */
const cutDescriptionToTitle = (description: string, oneLine: string): string => {
  const firstLineMatch = /^[^\r\n]*/.exec(description.replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, ''));
  const firstLine = firstLineMatch ? firstLineMatch[0].replace(/^ +| +$/g, '') : '';
  if (firstLine.length >= 15 && firstLine.length <= CHARGE_TITLE_MAX_LENGTH) {
    const title = firstLine.replace(TITLE_TRAILING_PUNCTUATION, '');
    if (title) {
      return title;
    }
  }
  const sentenceMatch = /^(.{15,50}?[.!?])(\s|$)/.exec(oneLine);
  if (sentenceMatch) {
    const title = sentenceMatch[1].replace(/\.+$/, '');
    // A sentence ending in "!" or "?" can be one character over.
    if (title && title.length <= CHARGE_TITLE_MAX_LENGTH) {
      return title;
    }
  }
  const wordsMatch = /^(.{20,})\s/.exec(oneLine.slice(0, CHARGE_TITLE_MAX_LENGTH - 1));
  if (wordsMatch) {
    const title = wordsMatch[1].replace(TITLE_TRAILING_PUNCTUATION, '');
    if (title) {
      return `${title}…`;
    }
  }
  return `${oneLine.slice(0, CHARGE_TITLE_MAX_LENGTH - 1)}…`;
};

/**
 * The title and description to show for a charge. Charges created before titles existed are shown the way
 * the backfill will save them, so nothing changes for the user when it runs: a short description becomes the
 * title, and a longer one is cut to a title without breaking a word and kept in full as the description.
 */
export const getChargeItemDisplay = (item: ChargeItemText): ChargeItemDisplay => {
  const description = item.description ? item.description.trim() : '';
  if (item.title) {
    // A charge whose title was copied from its description would otherwise show the same words twice.
    const repeatsTitle = description === item.title.trim();
    return { title: item.title, description: description && !repeatsTitle ? (item.description as string) : null };
  }
  if (!description) {
    return { title: CHARGE_TYPE_LABELS[item.type] || item.type, description: null };
  }
  // A title is one line, so line breaks and runs of spaces in the description become single spaces.
  const oneLine = description.replace(/\s+/g, ' ');
  if (oneLine.length <= CHARGE_TITLE_MAX_LENGTH) {
    return { title: oneLine, description: null };
  }
  return { title: cutDescriptionToTitle(item.description as string, oneLine), description: item.description as string };
};

/** The short label for a charge, for lists, pickers and other one-line uses. */
export const getChargeItemTitle = (item: ChargeItemText): string => getChargeItemDisplay(item).title;
